import styled, { css, keyframes } from 'styled-components';

export const spin = keyframes`
  from {
    --gradient-angle: 0deg;
  }
  to {
    --gradient-angle: 360deg;
  }
`;

export const borderWidth = keyframes`
  0%, 95% {
    padding: 2px;
  }
  100% {
    padding: 1px;
  }
`;

export const Gradient = styled.div`
  ${({ theme, $withoutMargin }) => css`
    background: linear-gradient(90deg, #2E93EE 0%, #2E93EE 6.25%, #2F92ED 12.5%, #3091ED 18.75%, #3190EC 25%, #338EEB 31.25%, #358CE9 37.5%, #3789E8 43.75%, #3A86E6 50%, #3D83E4 56.25%, #417FE1 62.5%, #457BDF 68.75%, #4976DC 75%, #4E71D9 81.25%, #536CD5 87.5%, #5866D2 93.75%, #5E60CE 100%);
    border-radius: 16px;
    margin: ${$withoutMargin ? '0px' : '0px 40px 0px 40px'};
    padding: 56px 0px 0px 0px;
    position: relative;
    width: ${$withoutMargin ? '100%' : 'calc(100% - 80px)'};

    @media (max-width: ${theme.v2.breakpoints.md}) {
      margin: 0px;
      width: 100%;
    }
  `}
`;

export const ConversationBox = styled.div`
  ${({ theme }) => css`
    border-radius: 16px 16px 0px 0px;
    padding: 0px 0px 16px 0px;
    background-color: ${theme.v2.colors.neutral.highPure};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 24px;

    @media (max-width: ${theme.v2.breakpoints.md}) {
      padding: 24px 0;
      gap: 16px;
    }
  `}
`;

export const WhiteBox = styled.div`
  ${({ theme }) => css`
    align-items: center;
    background-color: ${theme.v2.colors.neutral.highPure};
    display: flex;
    justify-content: center;
    overflow-x: hidden;
    padding: 0px 16px 16px;

    @media (max-width: ${theme.v2.breakpoints.md}) {
        overflow: auto;
        &::-webkit-scrollbar {
          display: none;
        }
        scrollbar-width: none;
    }
  `}
`;

export const AiChatFormAdapter = styled.div`
  ${({ theme }) => css`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 24px;
    @media (max-width: ${theme.v2.breakpoints.md}) {
      padding: 0 16px;
    }
  `}
`;

export const FormWrapper = styled.div`
  ${({ $disabled, $moreThanOneLine }) => css`
    position: relative;
    max-width: 800px;
    width: 100%;
    margin: -34px auto 0px;
    padding: 1px;
    z-index: 1;
    animation: ${borderWidth} 8s linear;

    @supports (background: conic-gradient(#000, #000)) {
      @property --gradient-angle {
        syntax: '<angle>';
        initial-value: 0deg;
        inherits: false;
      }
    }

    &::after, &::before{
      ${$disabled && css`
        background-image: none;
        display: none;
      `}

      content: '';
      position: absolute;
      height: 100%;
      width: 100%;
      background-image: conic-gradient(
        from var(--gradient-angle),
        #2E93EE 0%,
        #2E93EE 3%,
        #2F92ED 6%,
        #3091ED 9%,
        #3190EC 12%,
        #338EEB 15%,
        #358CE9 18%,
        #3789E8 21%,
        #3A86E6 24%,
        #3D83E4 27%,
        #417FE1 30%,
        #457BDF 33%,
        #4976DC 36%,
        #4E71D9 39%,
        #536CD5 42%,
        #5E60CE 45%,
        #5E60CE 48%,
        #5E60CE 51%,
        #5E60CE 54%,
        #5E60CE 57%,
        #5E60CE 60%,
        #5E60CE 63%,
        #5E60CE 66%,
        #3D83E4 69%,
        #3A86E6 72%,
        #3789E8 75%,
        #358CE9 78%,
        #338EEB 81%,
        #3190EC 84%,
        #3091ED 87%,
        #2F92ED 90%,
        #2E93EE 93%,
        #2E93EE 96%,
        #2E93EE 100%
      );
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: -1;
      border-radius: ${$moreThanOneLine ? '24px' : '2000px'};
      animation: ${spin} 4s linear infinite;
      animation-delay: 0s;
      animation-iteration-count: 2;
      animation-fill-mode: backwards;

    }

    &::before{
      filter: blur(16px);
      opacity: 0.2;
    }
  `}
`;

export const Form = styled.form`
  ${({
    theme, $disabled, $isFullScreen, $isRecording, $moreThanOneLine,
  }) => css`
    align-items: ${$moreThanOneLine ? 'flex-start' : 'center'};
    background: ${theme.v2.colors.neutral.highPure};
    border-radius: ${$moreThanOneLine ? '24px' : '2000px'};
    display: flex;
    flex-direction: row;
    flex-wrap: ${$isRecording ? 'nowrap' : 'wrap'};
    gap: 8px;
    justify-content: center;
    padding: 10px 12px;
    position: relative;
    width: 100%;
    z-index: 1;

    ${$disabled && css`
      background-color: ${theme.v2.colors.neutral.highLight};
      cursor: not-allowed;
    `}

    ${$isFullScreen && css`
      margin: 0px auto;
      max-width: 880px;
      width: 100%;
    `}
  `}
`;

const imgWidth = '32px';
const actionWidth = '100px';
export const TextArea = styled.textarea`
  ${({ theme }) => css`
    border: 0px;
    resize: none;
    outline: none;
    placeholder: ${theme.v2.colors.neutral.highMedium};
    font-family: ${theme.v2.font.family.primary};
    font-size: ${theme.v2.font.sizes.xs};
    font-weight: ${theme.v2.font.weights.regular};
    line-height: ${theme.v2.font.lineHeight.text};
    color: ${theme.v2.colors.neutral.lowDark};
    background: ${theme.v2.colors.neutral.highPure};
    width: calc(100% - ${imgWidth} - ${actionWidth});
    height: 24px;
    overflow: hidden;
    max-height: 200px;

    &:disabled {
      cursor: not-allowed;
      background: ${theme.v2.colors.neutral.highLight};
    }
  `}
`;

export const ActionsWrapper = styled.div`
  align-items: flex-end;
  display: flex;
  gap: 24px;
  margin-left: auto;
  width: fit-content;
`;

export const ButtonSubmitAdapter = styled.div`
  ${({ theme }) => css`
    display: flex;
    height: 36px;
    margin-left: auto;
    width: 36px;

    button {
      border-radius: 100%;
      height: 36px;
      padding: 0px;
      width: 36px;

      &:disabled {
          background-color: ${theme.v2.colors.neutral.highMedium};
      }
    }
  `}
`;

export const ButtonRecordingAdapter = styled.button`
  ${() => css`
    display: flex;
    height: 28px;
    margin-left: auto;
    width: fit-content;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border: none;
    background: none;
    padding: 0;

    &:disabled {
      cursor: not-allowed;
    }
  `}
`;

export const Image = styled.img`
  ${({ theme }) => css`
    border-radius: 50%;
    height: 24px;
    width: 24px;
    border: 1px solid ${theme.v2.colors.neutral.highMedium};
  `}
`;

export const BouncingAnimation = keyframes`
  0% {
    margin-left: 0px;
  }
  10% {
    margin-left: -20px;
  }
  20% {
    margin-left: 0px;
  }
  100% {
    margin-left: 0px;
  }
`;

export const SuggestionsWrapper = styled.div`
  ${({ theme, $interacted }) => css`
    display: flex;
    flex-direction: row;
    justify-content: center;
    gap: 16px;
    flex-wrap: wrap;
    max-width: 930px;
    width: 100%;
    padding: 0 24px;

    @media (max-width: ${theme.v2.breakpoints.md}) {
      padding: 0;
      padding-left: 56px;
      flex-direction: row;

      justify-content: flex-start !important;
      width: calc(100%);
      flex-wrap: nowrap;
      padding-right: 16px;

      &::-webkit-scrollbar {
        display: none;
      }
      scrollbar-width: none;

      ${!$interacted && css`
        animation: ${BouncingAnimation} 3s infinite;
      `}
    }

    @media (max-width: ${theme.v2.breakpoints.sm}) {
      padding-left: 16px;
    }
  `}
`;

export const TagWrapper = styled.div`
  left: 24px;      
  position: absolute;
  top: 18px;
`;

export const InfoWrapper = styled.div`
  right: 24px;      
  position: absolute;
  top: 18px;
`;
